import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';
import { siteInfo } from '@/mocks/site';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';

function formatViews(views: number): string {
  if (views >= 100000000) return `${(views / 100000000).toFixed(1)}亿`;
  if (views >= 10000) return `${(views / 10000).toFixed(0)}万`;
  return views.toString();
}

function ScrollToTopHandler() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

export default function Home() {
  const hotVideos = videos.filter((v) => v.isHot).slice(0, 6);
  const newVideos = videos.filter((v) => v.isNew).slice(0, 6);
  const featuredArticles = articles.filter((a) => a.isFeatured);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CN',
      addressLocality: '北京',
      streetAddress: siteInfo.address,
    },
    foundingDate: siteInfo.founded,
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteInfo.fullName,
    url: siteInfo.url,
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteInfo.url}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEOMeta
        title={siteInfo.fullName}
        description={siteInfo.description}
        keywords="国产在线一区,国产在线视频一区,国产高清在线一区,国产视频在线观看一区,国产精品在线一区,国产免费在线一区,国产在线一区最新视频"
        ogType="website"
        schema={[organizationSchema, websiteSchema]}
      />
      <ScrollToTopHandler />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <header className="relative h-[420px] md:h-[600px] w-full overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Chinese%20cinema%20theater%20interior%20with%20warm%20golden%20ambient%20lighting%2C%20luxurious%20velvet%20seats%2C%20large%20projection%20screen%20with%20soft%20glow%2C%20elegant%20atmosphere%2C%20rich%20burgundy%20and%20warm%20cream%20tones%2C%20cinematic%20mood%2C%20no%20text%20or%20logos&width=1920&height=1080&seq=home-hero-001&orientation=landscape"
            alt="国产在线一区 - 高清视频聚合平台"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center w-full px-4">
            <p className="text-accent-300 text-sm md:text-base font-medium tracking-widest mb-3 whitespace-nowrap">
              海量高清 · 即刻畅享
            </p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 text-center leading-tight mb-4">
              {siteInfo.fullName}
            </h1>
            <p className="text-background-50/80 text-sm md:text-lg max-w-2xl text-center leading-relaxed mb-8">
              {siteInfo.tagline}
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <Link
                to={`/category/${categories[0].slug}`}
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                开始浏览
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 border border-background-50/40 text-background-50 rounded-full text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </header>

        <main>
          {/* Category Navigation Section */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  视频分类
                </h2>
                <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
                  八大分类，覆盖影视综艺纪录片动漫等多元内容，总有一类适合您
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center gap-3 p-5 md:p-6 bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all cursor-pointer"
                  >
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100 group-hover:bg-primary-200 transition-colors">
                      <i className={`${cat.icon} text-xl md:text-2xl text-primary-600`}></i>
                    </div>
                    <span className="text-sm md:text-base font-medium text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                      {cat.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Hot Videos Section */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                    热门推荐
                  </h2>
                  <p className="text-foreground-500 text-sm">全网最受欢迎的高清视频内容</p>
                </div>
                <Link
                  to={`/category/${categories[0].slug}`}
                  className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer"
                >
                  查看全部
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
                {hotVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="relative w-full aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        title={`${video.title} - 国产在线一区高清视频`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      />
                      <div className="absolute top-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-1 rounded-md whitespace-nowrap">
                        {video.quality}
                      </div>
                      <div className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-1 rounded-md whitespace-nowrap">
                        {video.duration}
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90 opacity-0 group-hover:opacity-100 transition-opacity">
                          <i className="ri-play-fill text-background-50 text-xl"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1 mb-2">
                        {video.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                        {video.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-foreground-400">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-eye-line text-xs"></i>
                          </div>
                          <span className="text-xs">{formatViews(video.views)}</span>
                        </div>
                        <div className="flex items-center gap-0.5">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-star-fill text-accent-500 text-xs"></i>
                          </div>
                          <span className="text-xs text-accent-600 font-medium">{video.rating}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates Section */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                    最新更新
                  </h2>
                  <p className="text-foreground-500 text-sm">刚刚上线的最新视频内容</p>
                </div>
                <Link
                  to={`/category/${categories[1].slug}`}
                  className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer"
                >
                  查看全部
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
                {newVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.slug}`}
                    className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="relative w-full aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        title={`${video.title} - 国产在线一区最新视频`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      />
                      <div className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap">
                        NEW
                      </div>
                      <div className="absolute top-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-1 rounded-md whitespace-nowrap">
                        {video.quality}
                      </div>
                      <div className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-1 rounded-md whitespace-nowrap">
                        {video.duration}
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90 opacity-0 group-hover:opacity-100 transition-opacity">
                          <i className="ri-play-fill text-background-50 text-xl"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1 mb-2">
                        {video.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                        {video.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-400 whitespace-nowrap">
                          {video.publishDate}
                        </span>
                        <div className="flex items-center gap-0.5">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-star-fill text-accent-500 text-xs"></i>
                          </div>
                          <span className="text-xs text-accent-600 font-medium">{video.rating}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Articles Section */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  深度文章
                </h2>
                <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
                  行业深度解析、观影指南、幕后故事，带您走进视频内容的世界
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {featuredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group flex flex-col bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="w-full aspect-video overflow-hidden">
                      <img
                        src={article.thumbnail}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        {article.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="text-xs px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2 leading-snug">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-4 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="mt-auto flex items-center justify-between text-xs text-foreground-400">
                        <span className="whitespace-nowrap">{article.publishDate}</span>
                        <span className="whitespace-nowrap">{article.readTime}阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: siteInfo.usersCount, label: '累计用户', icon: 'ri-user-heart-line' },
                  { value: siteInfo.dailyActive, label: '日活跃用户', icon: 'ri-flashlight-line' },
                  { value: siteInfo.videoCount, label: '视频总量', icon: 'ri-film-line' },
                  { value: siteInfo.teamSize, label: '团队成员', icon: 'ri-team-line' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                      <i className={`${stat.icon} text-xl text-accent-600`}></i>
                    </div>
                    <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                      {stat.value}
                    </p>
                    <p className="text-xs md:text-sm text-foreground-500 whitespace-nowrap">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                立即开始您的观影之旅
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto mb-6">
                海量高清国产视频内容，持续更新，随时随地畅享观影体验
              </p>
              <Link
                to={`/category/${categories[0].slug}`}
                className="inline-flex px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即浏览
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}